import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Building2, Award, CheckCircle, XCircle,
  MapPin, Clock, ArrowRight, MessageCircle,
  ChevronRight, BookOpen, List, Columns2, Square,
} from 'lucide-react';
import { api, ApiError, type Plan, type PlanFeaturePoint, type Clinic, type Doctor, egp, type Subscription, type BlogPost } from '../api';
import { Card, Badge, PageLoader } from '../components/ui';
import { PlanBenefitList } from '../components/PlanBenefitList';
import { ChoosePlanCta } from '../components/ChoosePlanCta';
import { PlanOfferBadge, PlanPopularBadge, isMostPopularPlan } from '../components/PlanOfferBadge';
import { SmartCoverMedia } from '../components/SmartCoverMedia';
import { InternModal } from '../components/InternModal';
import { EarlyAccessInternModal, earlyAccessPromptShouldOpen } from '../components/EarlyAccessInternModal';
import { useAuth, startHref } from '../auth';

function useActiveSubscription() {
  const { me } = useAuth();
  return useQuery({
    queryKey: ['student-sub'],
    queryFn: () => api.get<{ subscription: Subscription | null }>('/api/student/subscription'),
    enabled: me?.role === 'student',
    staleTime: 30_000,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const UNSPLASH = {
  hero:        '/hero.jpeg',
  heroMobile:  '/hero-mobile.jpeg',
  therapy1:    'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=800&q=80',
  therapy2:    'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80',
  cta:         'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1920&q=80',
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 bg-teal-soft text-teal text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
      {children}
    </div>
  );
}

type BrowseLayout = 'list' | 'cols2' | 'cols1';

function browseLayoutClass(layout: BrowseLayout) {
  if (layout === 'list') return 'flex flex-col gap-3';
  if (layout === 'cols1') return 'grid grid-cols-1 gap-4 sm:gap-6';
  return 'grid grid-cols-2 gap-3 sm:gap-4';
}

function useBrowseLayout(storageKey: string) {
  const [layout, setLayoutState] = useState<BrowseLayout>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === 'list' || stored === 'cols2' || stored === 'cols1') return stored;
    } catch {
      /* ignore */
    }
    return 'list';
  });
  function setLayout(next: BrowseLayout) {
    setLayoutState(next);
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      /* ignore */
    }
  }
  return [layout, setLayout] as const;
}

function BrowseLayoutToggle({ value, onChange }: { value: BrowseLayout; onChange: (v: BrowseLayout) => void }) {
  const options = [
    { id: 'list' as const, label: 'List', Icon: List },
    { id: 'cols2' as const, label: 'Two columns', Icon: Columns2 },
    { id: 'cols1' as const, label: 'One column', Icon: Square },
  ];
  return (
    <div className="inline-flex shrink-0 items-center rounded-radius-sm border border-line bg-surface p-0.5" role="group" aria-label="Layout">
      {options.map(({ id, label, Icon }) => {
        const active = value === id;
        return (
          <button
            key={id}
            type="button"
            aria-label={label}
            aria-pressed={active}
            onClick={() => onChange(id)}
            className={`p-2 rounded-[8px] min-h-[36px] min-w-[36px] flex items-center justify-center touch-manipulation transition-colors ${
              active ? 'bg-teal text-white' : 'text-ink-soft hover:text-ink hover:bg-teal-soft/60'
            }`}
          >
            <Icon className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home page
// ---------------------------------------------------------------------------

export function HomePage() {
  const { me } = useAuth();
  const subQ = useActiveSubscription();
  const isSubscribed = me?.role === 'student' && subQ.data?.subscription?.status === 'active';
  const clubHref = me?.role === 'student' ? '/student' : me ? '/plans' : '/register';

  return (
    <div>
      <div
        className="lg:hidden pointer-events-none fixed inset-x-0 top-14 bottom-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(180deg, rgba(8,24,46,0.72) 0%, rgba(8,24,46,0.28) 42%, rgba(8,24,46,0.55) 100%), url(${UNSPLASH.heroMobile})` }}
        aria-hidden
      />
      <div
        className="hidden lg:block pointer-events-none fixed inset-x-0 top-14 bottom-0 z-0 bg-cover bg-right"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(8,24,46,0.82) 0%, rgba(8,24,46,0.42) 48%, rgba(8,24,46,0.12) 100%), url(${UNSPLASH.hero})` }}
        aria-hidden
      />

      <section className="relative z-[1] min-h-[calc(100svh-3.5rem+28vh)] flex items-center text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 w-full">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <p className="font-display text-sand text-xl sm:text-2xl mb-4 tracking-wide">
                PhysioEDGE
              </p>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-5 sm:mb-6 leading-tight text-white">
                You need time in a clinic, and someone to ask when you're stuck.
              </h1>
              <p className="text-white/75 text-base sm:text-lg mb-8 sm:mb-10 max-w-xl leading-relaxed">
                That's it. Book a week. Message a mentor. Read a short lesson if you want.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {isSubscribed ? (
                  <>
                    <Link
                      to="/student"
                      className="bg-sand text-white font-semibold px-7 py-4 rounded-radius hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2 text-center touch-manipulation min-h-[52px]"
                    >
                      Open club <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/blogs"
                      className="border border-white/35 text-white font-medium px-7 py-4 rounded-radius hover:bg-white/10 transition-colors inline-flex items-center justify-center text-center touch-manipulation min-h-[52px]"
                    >
                      Learn
                    </Link>
                  </>
                ) : me ? (
                  <>
                    <Link
                      to={clubHref}
                      className="bg-sand text-white font-semibold px-7 py-4 rounded-radius hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2 text-center touch-manipulation min-h-[52px]"
                    >
                      Start <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/blogs"
                      className="border border-white/35 text-white font-medium px-7 py-4 rounded-radius hover:bg-white/10 transition-colors inline-flex items-center justify-center text-center touch-manipulation min-h-[52px]"
                    >
                      Learn
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="bg-sand text-white font-semibold px-7 py-4 rounded-radius hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2 text-center touch-manipulation min-h-[52px]"
                    >
                      Join us <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/blogs"
                      className="border border-white/35 text-white font-medium px-7 py-4 rounded-radius hover:bg-white/10 transition-colors inline-flex items-center justify-center text-center touch-manipulation min-h-[52px]"
                    >
                      Learn
                    </Link>
                  </>
                )}
              </div>
              <p className="text-white/55 text-sm mt-4">You don't need to know a doctor to start.</p>
            </div>
            <HeroChatPreview />
          </div>
        </div>
      </section>

      <div className="relative z-10 bg-bg">
        <SoundFamiliar />
        <MentorStrip />
        <LearnPreview />
        <HowItWorksSimple />
        {!isSubscribed && <PlansPreview />}
      </div>
      <div className="relative z-0 h-[calc(100svh-3.5rem)]" aria-hidden />
    </div>
  );
}

function HeroChatPreview() {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/10 p-4 sm:p-5 space-y-3">
      <div className="ml-auto max-w-[92%]">
        <p className="text-[11px] text-white/50 mb-1 text-right">You</p>
        <div className="bg-sand text-white text-sm leading-relaxed rounded-2xl rounded-br-sm px-4 py-3">
          I keep watching other people get clinic placements through family. I'm in my last year and I've never been inside a real department. I don't know who to message, and I feel stupid asking in the group.
        </div>
      </div>
      <div className="mr-auto max-w-[92%]">
        <p className="text-[11px] text-white/50 mb-1">Mentor</p>
        <div className="bg-white text-ink text-sm leading-relaxed rounded-2xl rounded-bl-sm px-4 py-3">
          You're not the only one. You don't need an introduction. Pick a clinic, book a week, and show up. If something from that week is still stuck in your head, send it here.
        </div>
      </div>
    </div>
  );
}

function SoundFamiliar() {
  const cards = [
    {
      quote: "I don't know any doctors. I can't get into a clinic.",
      reply: "You don't have to know someone. You book a week.",
    },
    {
      quote: 'I have a question and nowhere to ask it.',
      reply: 'One question to a mentor who actually treats patients.',
    },
    {
      quote: "I'm drowning in PDFs and Telegram dumps.",
      reply: 'A few lessons you can actually finish.',
    },
  ];

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
      <h2 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-6 sm:mb-8">Sound familiar?</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
        {cards.map(card => (
          <div key={card.quote} className="rounded-2xl border border-line bg-surface p-5">
            <p className="text-ink text-sm sm:text-base leading-relaxed">“{card.quote}”</p>
            <p className="text-ink-soft text-sm mt-3 leading-relaxed">{card.reply}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function MentorStrip() {
  const { data, isLoading } = useQuery({
    queryKey: ['doctors-home'],
    queryFn: () => api.get<{ doctors: Doctor[] }>('/api/doctors?limit=2'),
    staleTime: 60_000,
  });
  if (isLoading) {
    return (
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4 sm:pb-6">
        <PageLoader variant="inline" />
      </section>
    );
  }
  const doctors = data?.doctors ?? [];
  if (doctors.length === 0) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4 sm:pb-6">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-ink">A name behind the door.</h2>
          <p className="text-ink-soft mt-2 text-sm sm:text-base">Pick someone. Ask one thing.</p>
        </div>
        <Link to="/doctors" className="hidden md:inline-flex items-center gap-2 text-sand-strong font-medium hover:underline shrink-0">
          Mentors <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {doctors.map(doctor => (
          <Link
            key={doctor.id}
            to={`/doctors/${doctor.id}`}
            className="flex items-center gap-4 p-4 bg-surface border border-line rounded-2xl hover:shadow-card transition-shadow touch-manipulation min-h-[88px]"
          >
            {doctor.photoUrl ? (
              <img src={doctor.photoUrl} alt="" className="w-14 h-14 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-sand-soft flex items-center justify-center text-sand-strong font-bold text-sm shrink-0">
                {doctor.fullName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-semibold text-ink truncate">{doctor.fullName}</div>
              {doctor.specialty && <div className="text-sm text-ink-soft truncate">{doctor.specialty}</div>}
            </div>
          </Link>
        ))}
      </div>
      <div className="text-center mt-5 md:hidden">
        <Link to="/doctors" className="text-sand-strong font-medium hover:underline">Mentors</Link>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Learn strip + how it works (home)
// ---------------------------------------------------------------------------

function LearnPreview() {
  const { data, isLoading } = useQuery({
    queryKey: ['blogs-home'],
    queryFn: () => api.get<{ posts: BlogPost[] }>('/api/blogs?limit=3'),
    staleTime: 60_000,
  });
  if (isLoading) {
    return (
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <PageLoader variant="inline" />
      </section>
    );
  }
  const posts = data?.posts ?? [];
  if (posts.length === 0) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
      <div className="flex items-end justify-between mb-7 sm:mb-10">
        <div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink">Learn</h2>
          <p className="text-ink-soft mt-2 text-sm sm:text-base">Short guides for clinic weeks and mentor questions.</p>
        </div>
        <Link to="/blogs" className="hidden md:inline-flex items-center gap-2 text-sand-strong font-medium hover:underline shrink-0">
          See all <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        {posts.map(post => (
          <Link
            key={post.id}
            to={`/blogs/${post.id}`}
            className="group block bg-surface rounded-2xl border border-line overflow-hidden hover:shadow-pop transition-all touch-manipulation"
          >
            <div className="relative bg-teal-soft overflow-hidden">
              {post.coverUrl ? (
                <SmartCoverMedia
                  src={post.coverUrl}
                  kind={post.coverKind}
                  alt={post.title}
                  learnFrame
                  playable={false}
                  poster={post.thumbnailUrl}
                />
              ) : (
                <div className="aspect-[16/9] w-full flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-teal" />
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="font-semibold text-ink group-hover:text-teal transition-colors">{post.title}</div>
            </div>
          </Link>
        ))}
      </div>
      <div className="text-center mt-6 md:hidden">
        <Link to="/blogs" className="text-sand-strong font-medium hover:underline">See all</Link>
      </div>
    </section>
  );
}

function HowItWorksSimple() {
  const lines = [
    { title: 'Read a short lesson', desc: 'A few pages you can actually finish.' },
    { title: 'Book a clinic week', desc: "You don't have to know someone anymore. You just have to be ready." },
    { title: 'Ask one question', desc: 'Send it to a mentor when something from clinic is still stuck.' },
  ];

  return (
    <section className="bg-bg border-y border-line py-14 sm:py-20 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto text-center mb-10">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink">How it works</h2>
        <p className="text-ink-soft mt-3 text-sm sm:text-base">Book a clinic week. That's the internship.</p>
      </div>
      <ol className="max-w-2xl mx-auto space-y-5">
        {lines.map((line, i) => (
          <li key={line.title} className="flex items-start gap-4 text-left">
            <span className="w-8 h-8 rounded-full bg-teal-soft text-teal text-sm font-bold flex items-center justify-center shrink-0">
              {i + 1}
            </span>
            <div>
              <div className="font-heading font-semibold text-ink">{line.title}</div>
              <p className="text-sm text-ink-soft mt-0.5">{line.desc}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Plans preview
// ---------------------------------------------------------------------------

function PlansPreview() {
  const { me } = useAuth();
  const subQ = useActiveSubscription();
  const isSubscribed = me?.role === 'student' && subQ.data?.subscription?.status === 'active';
  const { data, isLoading } = useQuery({
    queryKey: ['plans-preview'],
    queryFn: () => api.get<{ plans: Plan[]; sharedPoints: PlanFeaturePoint[] }>('/api/plans'),
  });

  if (isLoading) {
    return (
      <section className="py-8 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <PageLoader variant="inline" />
        </div>
      </section>
    );
  }
  const plans = data?.plans.slice(0, 3) ?? [];
  const sharedPoints = data?.sharedPoints ?? [];

  return (
    <section className="py-14 sm:py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-9 sm:mb-12">
          <SectionLabel>Plans</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-2">A plan if you want clinic weeks and questions.</h2>
          <p className="text-ink-soft text-sm sm:text-base">Weeks for internships and questions for mentors.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {plans.map((plan, i) => (
            <Card key={plan.id} className={`p-5 sm:p-6 flex flex-col relative overflow-hidden ${isMostPopularPlan(i) ? 'ring-2 ring-sand shadow-pop' : ''}`}>
              {isMostPopularPlan(i) && <PlanPopularBadge />}
              <PlanOfferBadge />
              <div className="font-heading font-semibold text-lg text-ink mb-1 pt-14 pr-2">{plan.name}</div>
              <div className="mb-4">
                {plan.compareAtPriceEgp > plan.priceEgp && (
                  <span className="text-sm line-through text-ink-faint mr-2">{egp(plan.compareAtPriceEgp)}</span>
                )}
                <span className="text-3xl font-bold text-sand">{egp(plan.priceEgp)}</span>
              </div>
              {plan.description && <p className="text-sm text-ink-soft mb-4">{plan.description}</p>}
              <PlanBenefitList plan={plan} sharedPoints={sharedPoints} />
              {isSubscribed ? (
                <Link
                  to="/clinics"
                  className={`text-center text-sm font-semibold py-3.5 rounded-radius-sm transition-opacity hover:opacity-90 touch-manipulation min-h-[48px] flex items-center justify-center ${isMostPopularPlan(i) ? 'bg-sand text-white' : 'bg-teal-soft text-teal'}`}
                >
                  Book a week
                </Link>
              ) : me && me.role !== 'student' ? (
                <Link
                  to="/plans"
                  className={`text-center text-sm font-semibold py-3.5 rounded-radius-sm transition-opacity hover:opacity-90 touch-manipulation min-h-[48px] flex items-center justify-center ${isMostPopularPlan(i) ? 'bg-sand text-white' : 'bg-teal-soft text-teal'}`}
                >
                  See plans
                </Link>
              ) : (
                <ChoosePlanCta
                  planId={plan.id}
                  className={`text-center text-sm font-semibold py-3.5 rounded-radius-sm transition-opacity hover:opacity-90 touch-manipulation min-h-[48px] flex items-center justify-center w-full ${isMostPopularPlan(i) ? 'bg-sand text-white' : 'bg-teal-soft text-teal'}`}
                >
                  {me?.role === 'student' ? 'Choose this plan' : 'Join us'}
                </ChoosePlanCta>
              )}
            </Card>
          ))}
        </div>
        <div className="text-center mt-7 sm:mt-8">
          <Link to="/plans" className="text-sand-strong text-sm font-medium hover:underline">See all plans</Link>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Plans page
// ---------------------------------------------------------------------------

export function PlansPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<{ plans: Plan[]; sharedPoints: PlanFeaturePoint[] }>('/api/plans'),
  });
  const { me } = useAuth();

  if (isLoading) return <PageLoader />;
  const plans = data?.plans ?? [];
  const sharedPoints = data?.sharedPoints ?? [];

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 sm:py-12">
      <div className="text-center mb-8 sm:mb-10">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-2">Your plan</h1>
        <p className="text-ink-soft text-sm sm:text-base">Internship weeks and mentor questions — pay once.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
        {plans.map((plan, i) => (
          <Card key={plan.id} className={`p-5 sm:p-6 flex flex-col relative overflow-hidden ${isMostPopularPlan(i) ? 'ring-2 ring-sand shadow-pop' : ''}`}>
            {isMostPopularPlan(i) && <PlanPopularBadge />}
            <PlanOfferBadge />
            <div className="font-heading font-semibold text-xl text-ink mb-1 pt-14 pr-4">{plan.name}</div>
            <div className="mb-4">
              {plan.compareAtPriceEgp > plan.priceEgp && (
                <span className="text-sm line-through text-ink-faint mr-2">{egp(plan.compareAtPriceEgp)}</span>
              )}
              <span className="text-3xl font-bold text-sand">{egp(plan.priceEgp)}</span>
            </div>
            {plan.description && <p className="text-sm text-ink-soft mb-4">{plan.description}</p>}
            <PlanBenefitList plan={plan} sharedPoints={sharedPoints} variant="rows" />
            {me?.role === 'student' ? (
              <ChoosePlanCta
                planId={plan.id}
                className={`mt-5 text-center text-sm font-semibold py-3.5 rounded-radius-sm hover:opacity-90 transition-opacity touch-manipulation min-h-[48px] flex items-center justify-center w-full ${isMostPopularPlan(i) ? 'bg-sand text-white' : 'bg-teal-soft text-teal'}`}
              >
                Choose this plan
              </ChoosePlanCta>
            ) : me ? (
              <Link to="/plans" className={`mt-5 text-center text-sm font-semibold py-3.5 rounded-radius-sm hover:opacity-90 transition-opacity block touch-manipulation min-h-[48px] flex items-center justify-center ${isMostPopularPlan(i) ? 'bg-sand text-white' : 'bg-teal-soft text-teal'}`}>
                See plans
              </Link>
            ) : (
              <ChoosePlanCta
                planId={plan.id}
                className={`mt-5 text-center text-sm font-semibold py-3.5 rounded-radius-sm hover:opacity-90 transition-opacity touch-manipulation min-h-[48px] flex items-center justify-center w-full ${isMostPopularPlan(i) ? 'bg-sand text-white' : 'bg-teal-soft text-teal'}`}
              >
                Join us
              </ChoosePlanCta>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clinics browse page
// ---------------------------------------------------------------------------

export function ClinicsPage() {
  const { me, loading: authLoading } = useAuth();
  const subQ = useActiveSubscription();
  const isStudent = me?.role === 'student';
  const sub = isStudent ? subQ.data?.subscription : undefined;
  const hasPlan = isStudent && sub?.status === 'active';
  const windowLocked = isStudent && sub?.status === 'expired' && !sub.startDate;
  const firstWeekBy = sub?.planClock?.mustBookFirstWeekBy ?? sub?.firstBookDeadline;
  const weeksLeft = hasPlan ? (sub!.internshipCreditsRemaining ?? 0) : 0;
  const subReady = !isStudent || !subQ.isPending;
  const showSubscribeCta = subReady && (!me || isStudent) && !windowLocked && (!hasPlan || weeksLeft <= 2);
  const [internClinicId, setInternClinicId] = useState<string | null>(null);
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (me && me.role !== 'student') {
      setEarlyAccessOpen(false);
      return;
    }
    if (earlyAccessPromptShouldOpen('internships')) setEarlyAccessOpen(true);
  }, [authLoading, me]);

  const { data, isLoading } = useQuery({
    queryKey: ['clinics'],
    queryFn: () => api.get<{ clinics: Clinic[]; total: number }>('/api/clinics?limit=50'),
  });

  const clinics = data?.clinics ?? [];
  const [layout, setLayout] = useBrowseLayout('physioedge.browseLayout.interns.v1');
  const isList = layout === 'list';

  return (
    <div>
      {earlyAccessOpen && (
        <EarlyAccessInternModal onClose={() => setEarlyAccessOpen(false)} />
      )}
      {internClinicId && isStudent && (
        <InternModal
          clinicId={internClinicId}
          weeksLeft={weeksLeft}
          onClose={() => setInternClinicId(null)}
        />
      )}
      <div className="sticky top-14 z-30 bg-bg border-b border-line">
        <div className="max-w-6xl mx-auto px-4 h-12 sm:h-14 flex items-center gap-2.5 sm:gap-4 min-w-0">
          <h1 className="font-heading text-lg sm:text-xl font-bold text-ink shrink-0">Internships</h1>
          {subReady && hasPlan && (
            <p className={`min-w-0 text-sm sm:text-base font-semibold tabular-nums ${weeksLeft <= 2 ? 'text-sand-strong' : 'text-ink-soft'}`}>
              <span className="font-heading text-base sm:text-lg text-ink">{weeksLeft}</span>
              {weeksLeft === 1 ? ' week left' : ' weeks left'}
            </p>
          )}
          {subReady && windowLocked && (
            <p className="min-w-0 truncate text-sm sm:text-base font-medium text-sand-strong">
              Booking is locked — message us to reopen.
            </p>
          )}
          {subReady && hasPlan && !sub?.startDate && firstWeekBy && (
            <p className="min-w-0 truncate text-sm font-medium text-ink-soft hidden md:block">
              Book your first week by {new Date(firstWeekBy).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}.
            </p>
          )}
          {subReady && !hasPlan && !windowLocked && (!me || isStudent) && (
            <p className="min-w-0 truncate text-sm sm:text-base font-medium text-ink-soft">
              <span className="sm:hidden">Unlock clinic weeks.</span>
              <span className="hidden sm:inline">Your clinic week is one plan away.</span>
            </p>
          )}
          {showSubscribeCta && (
            <Link
              to={startHref(me)}
              className="ml-auto shrink-0 bg-sand text-white text-sm font-semibold px-3.5 py-2 rounded-radius-sm hover:opacity-90 min-h-[40px] flex items-center justify-center touch-manipulation"
            >
              {hasPlan ? 'Add weeks' : 'Get a plan'}
            </Link>
          )}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
        <div className="flex items-center justify-between gap-3 mb-6 sm:mb-8">
          <p className="text-ink-soft text-sm sm:text-base min-w-0">
            Clinics that will take you for a week. Booking opens 20 October 2026. You then have 14 days to book your first week — that starts your plan time.
          </p>
          <BrowseLayoutToggle value={layout} onChange={setLayout} />
        </div>
        {isLoading && <PageLoader variant="lines" />}
        {!isLoading && clinics.length === 0 && <p className="text-ink-soft text-center py-8">No clinics listed yet.</p>}
        {!isLoading && (
        <div className={browseLayoutClass(layout)}>
          {clinics.map(clinic => {
            const isSquare = layout === 'cols1';
            const ctaClass = isList
              ? 'shrink-0 text-center text-sm font-semibold px-3.5 py-2 rounded-radius-sm hover:opacity-90 min-h-[40px] flex items-center justify-center touch-manipulation'
              : 'flex-1 text-center text-sm font-semibold py-3 rounded-radius-sm hover:opacity-90 min-h-[44px] flex items-center justify-center touch-manipulation';
            return (
          <div key={clinic.id} className={`rounded-2xl border border-line bg-surface overflow-hidden ${isList ? 'flex items-stretch' : 'flex flex-col'}`}>
            <Link
              to={`/clinics/${clinic.id}`}
              className={isList
                ? 'flex min-w-0 flex-1 items-center gap-3 p-3 sm:p-4 touch-manipulation'
                : 'group block touch-manipulation'}
            >
              <div className={isList
                ? 'w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden shrink-0 bg-teal-soft flex items-center justify-center'
                : `relative overflow-hidden bg-teal-soft ${isSquare ? 'h-52' : 'h-36 sm:h-44'}`}
              >
                {clinic.logoUrl ? (
                  <img
                    src={clinic.logoUrl}
                    alt=""
                    className={`w-full h-full object-cover ${isList ? '' : 'group-hover:scale-105 transition-transform duration-500'}`}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Building2 className={`${isList ? 'w-7 h-7' : 'w-16 h-16 sm:w-20 sm:h-20'} text-teal`} />
                  </div>
                )}
              </div>
              <div className={isList ? 'min-w-0 flex-1' : 'p-4 sm:p-5 pb-2'}>
                <div className={`font-heading font-semibold text-ink ${isList ? 'truncate' : 'text-base sm:text-lg'}`}>{clinic.name}</div>
                {clinic.description && (
                  <p className={`text-sm text-ink-soft ${isList ? 'mt-0.5 line-clamp-1' : 'mt-2 line-clamp-2'}`}>
                    {clinic.description}
                  </p>
                )}
              </div>
            </Link>
            <div className={isList ? 'p-3 flex items-center shrink-0' : 'px-3 sm:px-4 pb-3 sm:pb-4 mt-auto flex gap-2'}>
              {me?.role === 'student' ? (
                clinic.bookingEnabled === false ? (
                  <div className={`${ctaClass} bg-line text-ink-soft cursor-not-allowed`}>
                    Booking paused
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setInternClinicId(clinic.id)}
                    className={`${ctaClass} bg-sand text-white`}
                  >
                    Intern
                  </button>
                )
              ) : (
                <Link
                  to={me ? '/plans' : '/register'}
                  className={`${ctaClass} bg-teal-soft text-teal`}
                >
                  {me ? 'Open plan' : 'Join us'}
                </Link>
              )}
            </div>
          </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clinic detail page
// ---------------------------------------------------------------------------

export function ClinicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { me } = useAuth();
  const subQ = useActiveSubscription();
  const weeksLeft = me?.role === 'student'
    ? (subQ.data?.subscription?.status === 'active' ? (subQ.data.subscription.internshipCreditsRemaining ?? 0) : 0)
    : 0;
  const [showIntern, setShowIntern] = useState(false);

  const clinicQ = useQuery({
    queryKey: ['clinic', id],
    queryFn: () => api.get<{ clinic: Clinic & { description: string | null }; locations: any[] }>(`/api/clinics/${id}`),
    enabled: !!id,
  });

  if (clinicQ.isLoading) return <PageLoader variant="detail" />;
  if (clinicQ.isError) {
    const err = clinicQ.error;
    const notOnPlan = err instanceof ApiError && err.code === 'clinic_not_on_plan';
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Building2 className="w-12 h-12 text-ink-faint mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">
          {notOnPlan ? 'Not on your plan' : 'Clinic not found'}
        </h1>
        <p className="text-ink-soft text-sm mb-4 max-w-md mx-auto">
          {notOnPlan
            ? (err instanceof ApiError ? err.friendlyMessage : 'This clinic is not included in your plan.')
            : 'This clinic is unavailable.'}
        </p>
        <Link to="/clinics" className="text-sand-strong hover:underline text-sm">← Browse clinics</Link>
      </div>
    );
  }
  if (!clinicQ.data) return <div className="p-12 text-center text-ink-soft">Clinic not found.</div>;

  const { clinic, locations } = clinicQ.data;
  const bookingPaused = clinic.bookingEnabled === false;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12 pb-[calc(5.5rem+4rem+env(safe-area-inset-bottom,0px))] md:pb-28">
      {showIntern && id && me?.role === 'student' && !bookingPaused && (
        <InternModal clinicId={id} weeksLeft={weeksLeft} onClose={() => setShowIntern(false)} />
      )}
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-ink-faint mb-6 sm:mb-8">
        <Link to="/clinics" className="hover:text-ink">Clinics</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-ink">{clinic.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4 sm:gap-6 mb-7 sm:mb-8">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden bg-teal-soft shrink-0 flex items-center justify-center">
          {clinic.logoUrl ? (
            <img src={clinic.logoUrl} alt={clinic.name} className="w-full h-full object-cover" />
          ) : (
            <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-teal" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-1 leading-tight">{clinic.name}</h1>
          {clinic.description && <p className="text-ink-soft text-sm sm:text-base">{clinic.description}</p>}
        </div>
      </div>

      {/* Locations */}
      {locations.length > 0 && (
        <div className="mb-10 sm:mb-12">
          <h2 className="font-heading text-xl font-bold text-ink mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-teal" /> Locations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {locations.map((loc: any) => (
              <Card key={loc.id} className="p-4">
                <div className="font-semibold text-ink mb-1">{loc.name}</div>
                <div className="text-sm text-ink-soft flex items-start gap-1.5 mb-1">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-teal" />
                  {loc.addressLine}, {loc.city}
                </div>
                {loc.mapUrl && (
                  <a href={loc.mapUrl} target="_blank" rel="noreferrer" className="text-xs text-sand-strong hover:underline mt-2 inline-block">
                    View on map
                  </a>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* What to expect */}
      <div className="bg-teal-soft rounded-2xl p-5 sm:p-6 mb-2">
        <h3 className="font-heading font-semibold text-ink mb-3 flex items-center gap-2">
          <Award className="w-5 h-5 text-teal" /> What to expect
        </h3>
        <ul className="space-y-2 text-sm text-ink-soft">
          {[
            'You train with clinic staff beside you',
            'You see real patients and real cases',
            'You get a certificate when you finish',
            'The clinic rates your work',
          ].map(item => (
            <li key={item} className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success shrink-0" />{item}
            </li>
          ))}
        </ul>
      </div>

      {/* Sticky Intern CTA */}
      {me?.role !== 'doctor' && (
        <div
          className="fixed inset-x-0 z-30 border-t border-line bg-bg shadow-[0_-8px_24px_rgba(10,30,52,0.08)] md:!bottom-0"
          style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="max-w-5xl mx-auto px-4 py-3 md:py-4">
            {me?.role === 'student' ? (
              bookingPaused ? (
                <div className="w-full bg-line text-ink-soft font-semibold py-3.5 rounded-radius text-center text-sm min-h-[52px] flex items-center justify-center">
                  Booking paused at this clinic
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowIntern(true)}
                  className="w-full bg-sand text-white font-semibold py-3.5 rounded-radius text-center text-sm touch-manipulation min-h-[52px] flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                >
                  Intern
                </button>
              )
            ) : (
              <Link
                to={startHref(me)}
                className="w-full bg-sand text-white font-semibold py-3.5 rounded-radius text-center block text-sm touch-manipulation min-h-[52px] flex items-center justify-center hover:opacity-90 transition-opacity"
              >
                {me ? 'See plans to book' : 'Join us to book'}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Doctors browse page
// ---------------------------------------------------------------------------

export function DoctorsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['doctors'],
    queryFn: () => api.get<{ doctors: Doctor[]; total: number }>('/api/doctors?limit=50'),
  });
  const { me, loading: authLoading } = useAuth();
  const subQ = useActiveSubscription();
  const isStudent = me?.role === 'student';
  const sub = isStudent ? subQ.data?.subscription : undefined;
  const hasPlan = isStudent && sub?.status === 'active';
  const windowLocked = isStudent && sub?.status === 'expired' && !sub.startDate;
  const creditsLeft = hasPlan ? (sub!.consultationCreditsRemaining ?? 0) : 0;
  const subReady = !isStudent || !subQ.isPending;
  const showSubscribeCta = subReady && (!me || isStudent) && !windowLocked && (!hasPlan || creditsLeft <= 2);
  const [layout, setLayout] = useBrowseLayout('physioedge.browseLayout.mentors.v1');
  const isList = layout === 'list';
  const doctors = data?.doctors ?? [];
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (me && me.role !== 'student') {
      setEarlyAccessOpen(false);
      return;
    }
    if (earlyAccessPromptShouldOpen('mentors')) setEarlyAccessOpen(true);
  }, [authLoading, me]);

  return (
    <div>
      {earlyAccessOpen && (
        <EarlyAccessInternModal kind="mentors" onClose={() => setEarlyAccessOpen(false)} />
      )}
      <div className="sticky top-14 z-30 bg-bg border-b border-line">
        <div className="max-w-6xl mx-auto px-4 h-12 sm:h-14 flex items-center gap-2.5 sm:gap-4 min-w-0">
          <h1 className="font-heading text-lg sm:text-xl font-bold text-ink shrink-0">Ask a mentor</h1>
          {subReady && hasPlan && (
            <p className={`min-w-0 text-sm sm:text-base font-semibold tabular-nums ${creditsLeft <= 2 ? 'text-sand-strong' : 'text-ink-soft'}`}>
              <span className="font-heading text-base sm:text-lg text-ink">{creditsLeft}</span>
              {creditsLeft === 1 ? ' question left' : ' questions left'}
            </p>
          )}
          {subReady && windowLocked && (
            <p className="min-w-0 truncate text-sm sm:text-base font-medium text-sand-strong">
              Booking is locked — message us to reopen.
            </p>
          )}
          {subReady && !hasPlan && !windowLocked && (!me || isStudent) && (
            <p className="min-w-0 truncate text-sm sm:text-base font-medium text-ink-soft">
              <span className="sm:hidden">Unlock mentor questions.</span>
              <span className="hidden sm:inline">Your mentor questions are one plan away.</span>
            </p>
          )}
          {showSubscribeCta && (
            <Link
              to={startHref(me)}
              className="ml-auto shrink-0 bg-sand text-white text-sm font-semibold px-3.5 py-2 rounded-radius-sm hover:opacity-90 min-h-[40px] flex items-center justify-center touch-manipulation"
            >
              {hasPlan ? 'Add questions' : 'Get a plan'}
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
        <div className="flex items-center justify-between gap-3 mb-6 sm:mb-8">
          <p className="text-ink-soft text-sm sm:text-base min-w-0">
            Someone you can ask when you're stuck.
          </p>
          <BrowseLayoutToggle value={layout} onChange={setLayout} />
        </div>
        {isLoading && <PageLoader variant="lines" />}
        {!isLoading && doctors.length === 0 && <p className="text-ink-soft text-center py-8">No mentors listed yet.</p>}
        {!isLoading && (
        <div className={browseLayoutClass(layout)}>
          {doctors.map(doctor => {
            const askHref = `/student/consultations/new?doctorId=${doctor.id}`;
            const initials = doctor.fullName.split(' ').map((n: string) => n[0]).join('').slice(0, 2);
            const ctaClass = isList
              ? 'shrink-0 flex items-center justify-center min-h-[40px] px-3.5 py-2 text-sm font-semibold rounded-radius-sm hover:opacity-90 touch-manipulation'
              : 'flex w-full items-center justify-center min-h-[44px] text-sm font-semibold rounded-radius-sm hover:opacity-90 touch-manipulation';
            return (
              <div key={doctor.id} className={`bg-surface rounded-2xl border border-line overflow-hidden ${isList ? 'flex items-stretch' : 'flex flex-col'}`}>
                <Link to={`/doctors/${doctor.id}`} className={isList ? 'flex min-w-0 flex-1 items-center gap-3 p-3 touch-manipulation' : 'group block touch-manipulation'}>
                  <div className={isList
                    ? 'relative w-[4.5rem] h-[4.5rem] sm:w-20 sm:h-20 rounded-xl overflow-hidden shrink-0 bg-teal-soft/40'
                    : `relative overflow-hidden bg-teal-soft/40 ${layout === 'cols2' ? 'h-36 sm:h-44' : 'h-52'}`
                  }>
                    {doctor.photoUrl ? (
                      <img src={doctor.photoUrl} alt={doctor.fullName} className={`w-full h-full object-cover ${isList ? '' : 'group-hover:scale-105 transition-transform duration-500'}`} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className={`${isList ? 'w-10 h-10 text-sm' : 'w-16 h-16 sm:w-20 sm:h-20 text-xl sm:text-2xl'} rounded-full bg-white border-4 border-teal-soft flex items-center justify-center text-teal font-bold font-heading`}>
                          {initials}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={isList ? 'min-w-0 flex-1' : 'p-4 sm:p-5 pb-2'}>
                    <div className={`font-semibold text-ink ${isList ? 'truncate' : 'text-base sm:text-lg'}`}>{doctor.fullName}</div>
                    {doctor.specialty && <div className="text-sm text-sand-strong font-medium mt-0.5 truncate">{doctor.specialty}</div>}
                    {!isList && doctor.bio && <p className={`text-sm text-ink-soft mt-2 line-clamp-2 ${layout === 'cols2' ? 'hidden sm:block' : ''}`}>{doctor.bio}</p>}
                    <div className={`${isList ? 'mt-1' : 'mt-3'}`}>
                      {doctor.acceptingConsultations ? (
                        <Badge variant="success">Open</Badge>
                      ) : (
                        <Badge>Busy</Badge>
                      )}
                    </div>
                  </div>
                </Link>
                <div className={isList ? 'p-3 flex items-center shrink-0' : 'px-4 pb-4 mt-auto'}>
                  {me?.role === 'student' && doctor.acceptingConsultations ? (
                    <Link to={askHref} className={`${ctaClass} bg-sand text-white`}>
                      Ask
                    </Link>
                  ) : (
                    <Link to={`/doctors/${doctor.id}`} className={`${ctaClass} bg-teal-soft text-teal`}>
                      View
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Doctor detail page
// ---------------------------------------------------------------------------

export function DoctorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { me } = useAuth();
  const subQ = useActiveSubscription();
  const isSubscribed =
    me?.role === 'student' &&
    subQ.data?.subscription?.status === 'active' &&
    (subQ.data.subscription.consultationCreditsRemaining ?? 0) > 0;

  const doctorQ = useQuery({
    queryKey: ['doctor', id],
    queryFn: () => api.get<{ doctor: Doctor; clinics: any[] }>(`/api/doctors/${id}`),
    enabled: !!id,
  });

  if (doctorQ.isLoading) return <PageLoader variant="detail" />;
  if (!doctorQ.data) return <div className="p-12 text-center text-ink-soft">Doctor not found.</div>;

  const { doctor, clinics } = doctorQ.data;
  const uniqueClinics = Array.from(new Map((clinics ?? []).map((c: any) => [c.clinicId, c])).values());

  const consultLink = `/student/consultations/new?doctorId=${doctor.id}`;
  const creditsLeft = subQ.data?.subscription?.consultationCreditsRemaining ?? 0;
  const planCreditLabel = isSubscribed
    ? `${creditsLeft} question${creditsLeft === 1 ? '' : 's'} left`
    : 'Uses 1 question from your plan';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12 pb-[calc(7rem+4rem+env(safe-area-inset-bottom,0px))] md:pb-32">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-ink-faint mb-6 sm:mb-8">
        <Link to="/doctors" className="hover:text-ink">Mentors</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-ink">{doctor.fullName}</span>
      </div>

      {/* Profile header */}
      <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-6 mb-8 sm:mb-10">
        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-teal-soft shrink-0 flex items-center justify-center">
          {doctor.photoUrl ? (
            <img src={doctor.photoUrl} alt={doctor.fullName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-teal text-3xl font-bold font-heading">
              {doctor.fullName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
            </span>
          )}
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-ink mb-1">{doctor.fullName}</h1>
          {doctor.specialty && <div className="text-sand-strong font-medium mb-2">{doctor.specialty}</div>}
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="flex items-center gap-1.5 text-ink-soft">
              <Clock className="w-4 h-4 text-teal" />
              Answers within {doctor.responseWindowDays ?? 3} days
            </span>
            {doctor.acceptingConsultations ? (
              <Badge variant="success">Open</Badge>
            ) : (
              <Badge>Busy</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-7 sm:gap-8">
        {/* Left main */}
        <div className="lg:col-span-2 space-y-7 sm:space-y-8">
          {/* Bio */}
          {doctor.bio && (
            <section>
              <h2 className="font-heading text-xl font-bold text-ink mb-3">About</h2>
              <p className="text-ink-soft leading-relaxed">{doctor.bio}</p>
            </section>
          )}

          {/* Clinic associations */}
          {uniqueClinics.length > 0 && (
            <section>
              <h2 className="font-heading text-xl font-bold text-ink mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal" /> Works at
              </h2>
              <div className="space-y-3">
                {uniqueClinics.map((c: any) => (
                  <Link key={c.clinicId} to={`/clinics/${c.clinicId}`} className="flex items-center gap-4 p-4 bg-surface border border-line rounded-xl hover:shadow-card transition-shadow touch-manipulation min-h-[64px]">
                    <div className="w-10 h-10 rounded-lg bg-teal-soft flex items-center justify-center shrink-0">
                      {c.clinicLogoUrl ? (
                        <img src={c.clinicLogoUrl} alt={c.clinicName} className="w-full h-full rounded-lg object-cover" />
                      ) : (
                        <Building2 className="w-5 h-5 text-teal" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink">{c.clinicName}</div>
                      {c.locationName && (
                        <div className="text-xs text-ink-soft flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{c.locationName} — {c.locationCity}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-ink-faint shrink-0" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* How consultation works */}
          <section className="bg-bg rounded-2xl border border-line p-5 sm:p-6 mb-2">
            <h3 className="font-heading font-semibold text-ink mb-3">How asking works</h3>
            <p className="text-sm text-ink-soft mb-4">
              Ask one written question. Your mentor replies in writing — it uses 1 question from your plan.
            </p>
            <ol className="space-y-3 text-sm text-ink-soft">
              {[
                'Write one clear question and send it — uses 1 question from your plan.',
                `Your mentor answers within ${doctor.responseWindowDays ?? 3} days.`,
                'If they miss the deadline, that question is returned to your plan.',
                'Leave a rating to help other students.',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-teal-soft text-teal text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* Right sidebar — desktop: stacked card, mobile: shown above sticky bar */}
        <div className="space-y-4 hidden md:block">
          <Card className="p-5">
            <div className="text-sm text-ink-soft mb-1">{isSubscribed ? 'Questions left' : 'Your plan'}</div>
            <div className="font-heading text-2xl font-bold text-sand mb-4">
              {planCreditLabel}
            </div>
            {me?.role === 'student' && doctor.acceptingConsultations ? (
              <Link
                to={consultLink}
                className="w-full bg-sand text-white text-center text-sm font-semibold py-3 rounded-radius-sm hover:opacity-90 transition-opacity block touch-manipulation min-h-[48px] flex items-center justify-center"
              >
                <MessageCircle className="w-4 h-4 inline mr-2" />
                Ask
              </Link>
            ) : me?.role === 'student' && !doctor.acceptingConsultations ? (
              <div className="text-sm text-ink-faint text-center py-2">This mentor is busy right now</div>
            ) : me?.role === 'doctor' ? (
              <div className="text-sm text-ink-faint text-center py-2">Students ask mentors here</div>
            ) : (
              <Link
                to={startHref(me)}
                className="w-full bg-sand text-white text-center text-sm font-semibold py-3 rounded-radius-sm hover:opacity-90 transition-opacity block touch-manipulation min-h-[48px] flex items-center justify-center"
              >
                {me ? 'Open plan' : 'Join us to ask'}
              </Link>
            )}
          </Card>

          <Card className="p-5">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-ink-soft">Answer time</span>
                <span className="font-medium text-ink">{doctor.responseWindowDays ?? 3} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-soft">Status</span>
                {doctor.acceptingConsultations ? (
                  <span className="flex items-center gap-1 text-success text-xs font-medium">
                    <CheckCircle className="w-3.5 h-3.5" /> Open
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-ink-faint text-xs font-medium">
                    <XCircle className="w-3.5 h-3.5" /> Busy
                  </span>
                )}
              </div>
              {uniqueClinics.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-ink-soft">Clinic{uniqueClinics.length > 1 ? 's' : ''}</span>
                  <span className="font-medium text-ink">{uniqueClinics.length}</span>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Sticky consultation CTA — above bottom nav on mobile, viewport bottom on desktop */}
      {me?.role !== 'doctor' && (
        <div
          className="fixed inset-x-0 z-30 border-t border-line bg-bg shadow-[0_-8px_24px_rgba(10,30,52,0.08)] md:!bottom-0"
          style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="max-w-4xl mx-auto px-4 py-3 md:py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-ink-soft">{isSubscribed ? 'Questions left' : 'Your plan'}</span>
              <span className="font-bold text-sand text-sm">
                {planCreditLabel}
              </span>
            </div>
            {me?.role === 'student' && doctor.acceptingConsultations ? (
              <Link
                to={consultLink}
                className="w-full bg-sand text-white font-semibold py-3.5 rounded-radius text-center flex items-center justify-center gap-2 text-sm touch-manipulation min-h-[52px] hover:opacity-90 transition-opacity"
              >
                <MessageCircle className="w-4 h-4" /> Ask
              </Link>
            ) : me?.role === 'student' && !doctor.acceptingConsultations ? (
              <div className="w-full text-center text-sm text-ink-faint py-2">This mentor is busy right now</div>
            ) : (
              <Link
                to={startHref(me)}
                className="w-full bg-sand text-white font-semibold py-3.5 rounded-radius text-center block text-sm touch-manipulation min-h-[52px] flex items-center justify-center hover:opacity-90 transition-opacity"
              >
                {me ? 'Open plan' : 'Join us to ask'}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Certificate verification
// ---------------------------------------------------------------------------

export function VerifyCertPage() {
  const certId = window.location.pathname.split('/verify/')[1];
  const { data, isLoading, isError } = useQuery({
    queryKey: ['verify-cert', certId],
    queryFn: () => api.get<{ certificate: any }>(`/api/certificates/verify/${certId}`),
    enabled: !!certId && certId.length === 8,
  });

  if (isLoading) return <PageLoader variant="detail" />;

  const cert = data?.certificate;

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-6 sm:p-8 text-center">
        {isError || !cert ? (
          <>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-danger-soft mx-auto mb-4">
              <XCircle className="w-8 h-8 text-danger" />
            </div>
            <h1 className="font-heading text-xl font-bold text-danger mb-2">Certificate not found</h1>
            <p className="text-ink-soft text-sm">No certificate matches ID #{certId}. Double-check the number and try again.</p>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-success-soft mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-success mb-1">Certificate verified</h1>
            <p className="text-sm text-ink-soft mb-6">This is an authentic PhysioEDGE certificate.</p>
            <div className="text-left space-y-3 bg-bg rounded-radius p-4">
              <div className="flex justify-between text-sm"><span className="text-ink-soft">Student</span><span className="font-medium text-ink">{cert.studentName}</span></div>
              <div className="flex justify-between text-sm"><span className="text-ink-soft">Clinic</span><span className="font-medium text-ink">{cert.clinicName}</span></div>
              <div className="flex justify-between text-sm"><span className="text-ink-soft">Training</span><span className="font-medium text-ink">{cert.trainingStartDate} → {cert.trainingEndDate}</span></div>
              <div className="flex justify-between text-sm"><span className="text-ink-soft">Certificate ID</span><span className="font-mono font-medium text-ink">#{cert.certId}</span></div>
            </div>
            <a href={cert.pdfUrl} target="_blank" rel="noreferrer" className="mt-4 inline-block text-sand-strong text-sm font-medium hover:underline">
              Download PDF
            </a>
          </>
        )}
      </Card>
    </div>
  );
}
